import { useId, useRef, useState, useEffect, useMemo } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Bug,
  Camera,
  Check,
  ImagePlus,
  Info,
  Lightbulb,
  Send,
  X,
} from 'lucide-react';
import { BugApiError, submitBugReport, submitMemberBugReport } from '../../lib/api/bugs';
import { useT, type MessageKey } from '../../i18n';
import { getCapturedConsoleLog, __consoleLog__ } from '../../lib/consoleCapture';
import { getCapturedActions, getFailedRequests } from '../../lib/activityCapture';
import {
  getPageName,
  buildClaudePrompt,
  buildEmailBody,
  collectRuntimeInfo,
  redactCredentialParams,
} from './bugReportHelpers';
import { BUG_REPORT_EMAIL } from './config';
import { clubSession, memberSession } from '../../lib/auth';
import { currentImpersonation } from '../../lib/api/impersonation';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';

// ─────────────────────────────────────────────────────────────────────
// BugReportModal — the bug/feature report dialog. Extracted verbatim from the
// monolith (legacy/App.jsx). Markup (classNames, Norwegian strings, structure)
// is preserved exactly.
//
// SECRET HANDLING: BUG_REPORT_EMAIL used to be a module-level const in the
// monolith, then a prop. It now comes from ./config (VITE_* env via
// import.meta.env) — no value lives in source. It is only the address offered
// for a MANUAL send.
//
// NO E-MAIL FROM THE BROWSER (#1142, owner decision 2026-11-02). The dialog
// used to post a copy of every report to Web3Forms with a key baked into the
// bundle. The report is filed with the API, and the API mails the heads-up
// through its own mail driver — so the browser sends nothing but the report.
// ─────────────────────────────────────────────────────────────────────

// THE DRAFT OUTLIVES THE DIALOG. Reporting from the wrong page is common, and
// the advice is «close, go to the page with the fault, open again» — which is
// only reasonable if what was written survives. Kept in memory for the session
// (not across a reload): nothing here belongs in persistent storage until sent.
const EMPTY_DRAFT = { kind: 'bug', title: '', description: '', screenshot: null as any };

// ── THE DRAFT BELONGS TO WHOEVER WROTE IT (#719 review) ─────────────
//
// It is a module global holding free text and a base64 SCREENSHOT of whatever
// was on the reporter's screen. Keeping that across the dialog closing is the
// feature; keeping it across a change of PERSON is a leak — sign out, switch
// persona, change organization or start an impersonation, and the next context
// would open the dialog on the previous one's unsent report.
//
// KEYED RATHER THAN CLEARED AT EACH BOUNDARY, deliberately. A list of «call
// `clearReportDraft()` here» sites is a list somebody forgets to extend the next
// time a way to change identity is added — and the failure is silent. An owner
// key cannot be forgotten: a draft written under one key is simply never read
// under another.
const draftOwner = () => {
  const session = clubSession.state();
  const impersonation = currentImpersonation();
  // THE MEMBER TOO (2026-10-22): the member app files reports now, and two
  // players taking turns on one phone are two people.
  const member = memberSession.state().member;
  return [
    session.account?.id ?? 'anon',
    member?.id ?? 'no-member',
    session.currentScope?.tenantId ?? 'no-scope',
    // The impersonation ID, not the organization: two impersonations of the same
    // organization are still two different contexts.
    impersonation?.impersonationId ?? 'self',
  ].join('|');
};

export const reportDraft = { ...EMPTY_DRAFT, owner: '' };

export function clearReportDraft() {
  Object.assign(reportDraft, EMPTY_DRAFT, { owner: draftOwner() });
}

/**
 * Take ownership of the kept draft, discarding it if it is somebody else's.
 *
 * Idempotent: it resets only when the owner differs, so calling it on every
 * render — including StrictMode's doubled one — changes nothing.
 */
export function claimReportDraft() {
  const owner = draftOwner();
  if (reportDraft.owner !== owner) Object.assign(reportDraft, EMPTY_DRAFT, { owner });
}

export interface BugReportModalProps {
  currentView: any;
  currentContext: any;
  onClose: any;
  onOpenArchive?: any;
  /**
   * Which door files the report (owner decision 2026-10-22): the portal's
   * (`staff`, the organization it is acting in) or the member app's
   * (`member`, the member's own club). Defaults to the portal.
   */
  mode?: 'staff' | 'member';
}

/** The sentence for a refused report, by the server's code. */
const REFUSAL_KEY: Record<string, MessageKey> = {
  bug_reports_closed: 'bugs.report.refused.closed',
  too_many_attempts: 'bugs.report.refused.rateLimited',
  invalid_report: 'bugs.report.refused.invalid',
  report_too_large: 'bugs.report.refused.tooLarge',
};

export function BugReportModal({
  currentView,
  currentContext,
  onClose,
  onOpenArchive,
  mode = 'staff',
}: BugReportModalProps) {
  const t = useT();
  // Why the report was not saved, as a catalog key — null while it was.
  const [refusal, setRefusal] = useState<MessageKey | null>(null);
  const bugReportEmail = BUG_REPORT_EMAIL;
  // The form starts from the kept draft, so «close, go to the right page, open
  // again» does not lose what was written.
  // BEFORE the state initialisers below read it. See `claimReportDraft`: a draft
  // written by another account, scope or impersonation is discarded here rather
  // than shown to whoever opened the dialog next.
  claimReportDraft();
  const [kind, setKind] = useState(reportDraft.kind); // 'bug' | 'feature'
  const [title, setTitle] = useState(reportDraft.title);
  const [description, setDescription] = useState(reportDraft.description);
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState<any>(null); // null | { locally }
  const [copyStatus, setCopyStatus] = useState(''); // 'copied' | 'error' | ''
  // Screenshot — base64 data URL. Can come from manual upload or auto-capture.
  // Image is resized to a max 1280px longest side to keep Storage under the limit.
  const [screenshot, setScreenshot] = useState<any>(reportDraft.screenshot);
  const [screenshotError, setScreenshotError] = useState('');
  const [capturing, setCapturing] = useState(false);
  // Seconds to wait before the frame is grabbed (0 = «Nå»), and whether the
  // dialog is currently stepped out of the picture for that grab.
  const [captureDelay, setCaptureDelay] = useState(3);
  const [hiddenForCapture, setHiddenForCapture] = useState(false);
  const uid = safeId('bug-report', useId());
  const ids = {
    title: `${uid}-title`,
    lead: `${uid}-lead`,
    titleField: `${uid}-title-field`,
    description: `${uid}-description`,
    descriptionHint: `${uid}-description-hint`,
  };
  // Focus starts in «Kort tittel», where the old `autoFocus` put it.
  const titleFieldRef = useRef<HTMLInputElement | null>(null);

  // Keep the draft current. Once sent, the draft is gone — the next report
  // starts empty.
  useEffect(() => {
    if (saved) return;
    reportDraft.kind = kind;
    reportDraft.title = title;
    reportDraft.description = description;
    reportDraft.screenshot = screenshot;
    // STAMPED ON EVERY SAVE, not only on claim: the identity can change while
    // the dialog is open, and what is being typed now belongs to whoever is
    // signed in now.
    reportDraft.owner = draftOwner();
  }, [kind, title, description, screenshot, saved]);

  const hasDraft = !!(title.trim() || description.trim() || screenshot);
  const discardDraft = () => {
    clearReportDraft();
    setKind('bug');
    setTitle('');
    setDescription('');
    setScreenshot(null);
    setScreenshotError('');
  };

  // Build the page name based on the view
  const pageName = getPageName(currentView);

  // Build the "write to Claude" text based on the page
  const claudePrompt = buildClaudePrompt(
    pageName,
    currentView,
    currentContext,
    title,
    description,
    kind,
  );
  const emailBody = buildEmailBody(pageName, title, description, claudePrompt, kind);

  // Resize the image to a max 1280px longest side. Preserves the aspect ratio.
  // Images larger than this bloat Storage (base64 is ~33% larger).
  const resizeImage = async (dataUrl: any, maxSize = 1280) => {
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = () => reject(new Error('Kunne ikke laste bilde'));
      img.src = dataUrl;
    });
    const ratio = Math.min(maxSize / img.width, maxSize / img.height, 1);
    if (ratio === 1) return dataUrl; // the image is already small enough
    const targetW = Math.round(img.width * ratio);
    const targetH = Math.round(img.height * ratio);
    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d')!;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, targetW, targetH);
    // JPEG for bug screenshots — quality 0.85 gives good compression without
    // visible loss. PNG would bloat the file 3-5x.
    return canvas.toDataURL('image/jpeg', 0.85);
  };

  // Manual upload — from the file input
  const handleUpload = async (e: any) => {
    setScreenshotError('');
    const file = e.target.files?.[0];
    e.target.value = ''; // reset the input to allow re-upload of the same file
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setScreenshotError('Filen må være et bilde (PNG, JPG eller WebP).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setScreenshotError('Filen er for stor (maks 10 MB). Komprimér først.');
      return;
    }

    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Kunne ikke lese fil'));
        reader.readAsDataURL(file);
      });
      const resized = await resizeImage(dataUrl);
      setScreenshot(resized);
    } catch (err) {
      console.error('[BugReport] Upload-feil:', err);
      setScreenshotError('Kunne ikke behandle bildet. Prøv et annet.');
    }
  };

  // Screenshot capture with an optional delay. The reporter HIDES ITSELF while
  // the frame is taken — on «Nå» too — so the picture is the page behind it,
  // not this dialog. The delay leaves time to open a menu or hover over the
  // fault before the frame is grabbed. The browser's share picker comes FIRST
  // (it needs the click as a user gesture); the countdown starts after it.
  const handleAutoCapture = async () => {
    setScreenshotError('');
    setCapturing(true);
    let stream: MediaStream | null = null;
    const originalTitle = document.title;

    try {
      if (!navigator.mediaDevices?.getDisplayMedia) {
        throw new Error('Skjermdeling støttes ikke i denne nettleseren.');
      }

      // Request screen access. The user gets a picker and MUST approve.
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'browser' },
        audio: false,
        preferCurrentTab: true, // Chrome hint to suggest this tab first
      } as any);

      // Create a video element; its CURRENT frame becomes the still image.
      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      await video.play();
      await new Promise<void>((r) => {
        if (video.videoWidth) {
          r();
          return;
        }
        video.onloadedmetadata = () => r();
      });

      // Step out of the picture, then wait. The countdown lives in the tab
      // title — anything drawn on the page would end up in the screenshot.
      setHiddenForCapture(true);
      for (let left = captureDelay; left > 0; left--) {
        document.title = `📸 ${left} …`;
        await new Promise((r) => setTimeout(r, 1000));
      }
      document.title = originalTitle;
      // Let the hidden dialog and the share picker leave the frame before the
      // grab — a repaint plus a short settle is what «Nå» means in practice.
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      await new Promise((r) => setTimeout(r, captureDelay === 0 ? 400 : 150));

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(video, 0, 0);
      video.srcObject = null;

      const raw = canvas.toDataURL('image/jpeg', 0.85);
      const resized = await resizeImage(raw);
      setScreenshot(resized);
    } catch (err: any) {
      console.error('[BugReport] Capture-feil:', err);

      // Check whether we are in an iframe — iframes typically block getDisplayMedia
      // entirely (immediate NotAllowedError, no dialog shown). That is not the
      // same as the user actually declining.
      let inIframe = false;
      try {
        inIframe = window.self !== window.top;
      } catch {
        inIframe = true;
      }

      if (err.name === 'NotAllowedError') {
        if (inIframe) {
          setScreenshotError(
            'Auto-capture er blokkert i denne nettleser-rammen (sandboxed iframe). ' +
              'Bruk manuell upload nedenfor — du kan ta skjermbilde med ' +
              (navigator.platform.includes('Mac') ? 'Cmd+Shift+4' : 'PrtScn eller Win+Shift+S') +
              ' og laste det opp.',
          );
        } else {
          setScreenshotError(
            'Skjermdeling ble avslått. Klikk «Ta skjermbilde» igjen og velg ' +
              'hvilken fane/vindu som skal fanges. Eller last opp et bilde manuelt.',
          );
        }
      } else if (
        err.name === 'NotSupportedError' ||
        /not supported|getDisplayMedia/i.test(err.message)
      ) {
        setScreenshotError(
          'Auto-capture er ikke støttet i denne nettleseren. Bruk manuell upload i stedet.',
        );
      } else if (err.name === 'NotFoundError') {
        setScreenshotError('Ingen skjerm tilgjengelig å dele. Prøv manuell upload.');
      } else {
        setScreenshotError(
          `Kunne ikke ta skjermbilde: ${err.message || err.name}. Bruk manuell upload.`,
        );
      }
    } finally {
      // Always stop sharing and come back — also when the grab failed.
      stream?.getTracks().forEach((t) => t.stop());
      document.title = originalTitle;
      setHiddenForCapture(false);
      setCapturing(false);
    }
  };

  // Paste an image from the clipboard (typically after Print Screen → Cmd/Ctrl+V).
  // Listens globally while the modal is open.
  useEffect(() => {
    const handlePaste = async (e: any) => {
      // Ignore if we already have an image
      if (screenshot) return;
      // Ignore if the user is pasting into a text field with text
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type && item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (!file) continue;

          try {
            const dataUrl = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = () => reject(new Error('Kunne ikke lese fil'));
              reader.readAsDataURL(file);
            });
            const resized = await resizeImage(dataUrl);
            setScreenshot(resized);
            setScreenshotError('');
          } catch (err) {
            console.error('[BugReport] Paste-feil:', err);
            setScreenshotError('Kunne ikke lime inn bilde. Prøv å laste opp manuelt.');
          }
          return;
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [screenshot]);

  const handleSave = async () => {
    if (!title.trim() || !description.trim()) return;
    setSubmitting(true);
    let savedLocally = false;

    // File the report — ONE report, through the door this surface uses (owner
    // decision 2026-10-22). The server mints the id, the time, the status and
    // the reporter, and mails the heads-up (#1142); off the http backend it
    // goes to the local list as before.
    setRefusal(null);
    try {
      const bug = {
        kind: kind as 'bug' | 'feature', // used for styling and filtering in the bug archive
        title: title.trim(),
        description: description.trim(),
        page: pageName,
        view: currentView,
        context: currentContext || {},
        screenshot: screenshot || null, // base64 data URL, or null if none
        consoleLog: getCapturedConsoleLog() || null, // console log from the session
        // The trail to the bug: recent clicks/navigations (labels, never typed
        // values) and API calls that failed — lib/activityCapture.ts.
        actions: getCapturedActions(),
        failedRequests: getFailedRequests(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        // NO CREDENTIAL IN THE REPORT (#1080): the invitation landing keeps its
        // token in the address bar, and a report filed there must not carry it.
        url:
          typeof window !== 'undefined' ? redactCredentialParams(window.location?.href ?? '') : '',
        // Snapshot of the environment AS REPORTED — viewport, app version, browser,
        // storage backend. Captured here because it cannot be recovered later, and
        // it is what decides whether a layout bug reproduces.
        runtime: collectRuntimeInfo(),
      };

      const filed = await (mode === 'member' ? submitMemberBugReport : submitBugReport)(bug);
      console.log('[Padel24] ' + (kind === 'feature' ? 'Forslag' : 'Bug') + ' lagret:', filed.id);
      savedLocally = true;
      clearReportDraft(); // sent — the next report starts empty
    } catch (e) {
      console.error('[Padel24] Kunne ikke lagre rapporten:', e);
      setRefusal((e instanceof BugApiError && REFUSAL_KEY[e.code]) || 'bugs.report.refused.failed');
    }

    setSaved({ locally: savedLocally });
    setSubmitting(false);
  };

  // Mailto URL — used as an anchor tag (not window.open)
  const mailtoUrl = useMemo(() => {
    if (!title.trim() || !description.trim()) return '';
    const subject = encodeURIComponent(`[Padel24 Bug] ${title}`);
    const body = encodeURIComponent(emailBody);
    return `mailto:${bugReportEmail}?subject=${subject}&body=${body}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, emailBody]);

  const handleCopy = async (text: any) => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        setCopyStatus('copied');
        setTimeout(() => setCopyStatus(''), 2000);
      } else {
        // Fallback: create a textarea and select it
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopyStatus('copied');
        setTimeout(() => setCopyStatus(''), 2000);
      }
    } catch (e) {
      console.error('[Padel24] Kunne ikke kopiere:', e);
      setCopyStatus('error');
      setTimeout(() => setCopyStatus(''), 2000);
    }
  };

  const canSubmit = title.trim() && description.trim() && !submitting;

  // What rides along, counted for the banner. Warnings are only console noise,
  // so «konsollfeil» counts errors, uncaught errors and rejections.
  const consoleErrorCount = __consoleLog__.filter((e: any) => e.level !== 'warn').length;
  const failedRequestCount = getFailedRequests().length;
  const actionCount = getCapturedActions().length;

  // Soft warnings — they never block sending, they ask for the two things that
  // most often make a report unusable: too little text, and no picture.
  const MIN_DESCRIPTION = 40;
  const warnShortDescription =
    title.trim().length > 0 && description.trim().length < MIN_DESCRIPTION;
  const warnNoScreenshot = kind === 'bug' && !screenshot && title.trim().length > 0;

  const isFeature = kind === 'feature';
  const isMac = typeof navigator !== 'undefined' && navigator.platform.includes('Mac');
  const sectionLabel = 'block text-sm font-semibold text-ink mb-1.5';

  return (
    // data-bug-reporter: activity capture ignores clicks in here. While a
    // screenshot is being taken the whole overlay is invisible AND lets clicks
    // through, so a menu can be opened during the countdown.
    //
    // ON A WRAPPER WITH NO BOX (`display: contents`) since the overlay became a
    // `Dialog` (#900): `Dialog` takes no arbitrary attributes, and both of
    // these only need an ancestor — `closest('[data-bug-reporter]')` finds it,
    // and `visibility` is inherited.
    <div
      data-bug-reporter=""
      style={
        hiddenForCapture ? { display: 'contents', visibility: 'hidden' } : { display: 'contents' }
      }
    >
      {/* A `Dialog` (#900): named by its title, described by the page it
          reports from. Escape closes it like «Lukk» (the draft is kept) —
          except during the screenshot countdown, when the dialog is invisible
          and an Escape is far more likely meant for a menu on the page. */}
      <Dialog
        labelledBy={ids.title}
        describedBy={ids.lead}
        onClose={() => {
          if (!hiddenForCapture) onClose();
        }}
        showClose={false}
        initialFocus={titleFieldRef}
        backdropClassName="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
        className="w-full max-w-3xl flex flex-col rounded-2xl bg-white shadow-2xl overflow-hidden"
        style={{ maxHeight: '92vh' }}
      >
        {/* Header — tinted by kind: rose for a bug, lime for a suggestion */}
        <div
          className={`flex items-center justify-between gap-3 px-6 py-4 border-b flex-shrink-0 ${
            isFeature ? 'bg-lime-50 border-lime-100' : 'bg-rose-50 border-rose-100'
          }`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                isFeature ? 'bg-lime-100 text-lime-700' : 'bg-rose-100 text-rose-600'
              }`}
            >
              {isFeature ? <Lightbulb size={20} /> : <Bug size={20} />}
            </div>
            <div className="min-w-0">
              <h3 id={ids.title} className="font-display text-lg font-bold leading-tight text-ink">
                {isFeature ? 'Foreslå en endring' : 'Meld en bug'}
              </h3>
              <p id={ids.lead} className="text-xs text-ink-2 truncate">
                Fra siden: <strong className="text-ink">{pageName}</strong>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0">
            {onOpenArchive && (
              <button
                onClick={() => {
                  onOpenArchive();
                  onClose();
                }}
                className="text-sm font-semibold text-ink-2 hover:text-ink"
              >
                Alle saker →
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-1 text-ink-2 hover:text-ink"
              aria-label="Lukk"
              title="Lukk — utkastet beholdes"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {!saved ? (
            <>
              {/* Stand on the page where the fault is — the report reads THIS page */}
              <div className="rounded-xl p-4 bg-amber-50 border border-amber-200 text-[13px] leading-relaxed text-ink">
                <div className="flex items-start gap-2.5">
                  <AlertTriangle size={16} className="flex-shrink-0 mt-0.5 text-ink" />
                  <div>
                    <strong>Stå på siden der feilen er.</strong> Rapporten henter data fra siden du
                    står på nå — <strong className="text-amber-700">{pageName}</strong> — slik den
                    ser ut akkurat nå, og skjermbildet tas av denne siden. Er feilen et annet sted:
                    lukk vinduet (utkastet beholdes), gå til siden med feilen og åpne rapporten der.
                  </div>
                </div>
                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-3 py-1.5 rounded-lg bg-white border border-amber-200 text-xs font-semibold text-ink hover:bg-amber-100"
                  >
                    Lukk og gå til riktig side
                  </button>
                </div>
              </div>

              {/* What rides along automatically */}
              <div className="rounded-xl p-3.5 bg-sky-50 border border-sky-100 text-[13px] leading-relaxed text-ink-2 flex items-start gap-2.5">
                <Info size={16} className="flex-shrink-0 mt-0.5 text-ink-2" />
                <div>
                  Følger med automatisk fra siden: tilstanden i portalen, nettleser og
                  skjermstørrelse, <strong className="text-ink">{consoleErrorCount}</strong>{' '}
                  konsollfeil, <strong className="text-ink">{failedRequestCount}</strong> feilede
                  kall og de <strong className="text-ink">{actionCount}</strong> siste handlingene
                  dine — og skjermbildet hvis du legger ved. Meld helst fra{' '}
                  <strong className="text-ink">rett etter at feilen skjedde</strong>, så er
                  handlingene som førte dit med.
                </div>
              </div>

              {/* Kind: bug or suggestion */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setKind('bug')}
                  aria-pressed={!isFeature}
                  className={`p-4 rounded-xl border-2 text-left transition-colors flex items-start gap-3 ${
                    !isFeature
                      ? 'border-rose-500 bg-rose-50'
                      : 'border-line bg-white hover:bg-bg-soft'
                  }`}
                >
                  <Bug size={20} className="flex-shrink-0 text-rose-600" />
                  <div>
                    <div className="font-display text-sm font-bold text-ink">Bug</div>
                    <div className="text-xs text-ink-2">
                      Noe virker ikke, viser feil tall eller ser feil ut
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setKind('feature')}
                  aria-pressed={isFeature}
                  className={`p-4 rounded-xl border-2 text-left transition-colors flex items-start gap-3 ${
                    isFeature
                      ? 'border-lime-600 bg-lime-50'
                      : 'border-line bg-white hover:bg-bg-soft'
                  }`}
                >
                  <Lightbulb size={20} className="flex-shrink-0 text-lime-700" />
                  <div>
                    <div className="font-display text-sm font-bold text-ink">Forslag</div>
                    <div className="text-xs text-ink-2">
                      En idé, en forbedring eller noe som mangler
                    </div>
                  </div>
                </button>
              </div>

              {/* Title */}
              <div>
                <label htmlFor={ids.titleField} className={sectionLabel}>
                  Kort tittel
                </label>
                <input
                  ref={titleFieldRef}
                  id={ids.titleField}
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="input w-full text-sm"
                  placeholder={
                    isFeature
                      ? 'F.eks. «Eksport av resultater til Excel»'
                      : 'F.eks. «Resultatet lagres ikke når jeg trykker Lagre kamp»'
                  }
                />
              </div>

              {/* Description */}
              <div>
                <label htmlFor={ids.description} className={sectionLabel}>
                  Beskrivelse
                </label>
                <textarea
                  id={ids.description}
                  // The «too short» nudge describes this field, so it is tied
                  // to it rather than left as a sentence nearby.
                  aria-describedby={warnShortDescription ? ids.descriptionHint : undefined}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="input w-full text-sm"
                  rows={5}
                  placeholder={
                    isFeature
                      ? 'Hva ønsker du deg, og hvorfor? Hvordan ville du brukt det?'
                      : 'Hva gjorde du? Hva skjedde? Hva forventet du i stedet?'
                  }
                />
                {warnShortDescription && (
                  <p
                    id={ids.descriptionHint}
                    className="mt-1.5 flex items-center gap-1.5 text-xs text-amber-800"
                  >
                    <AlertCircle size={13} className="flex-shrink-0" />
                    Beskrivelsen er kort —{' '}
                    {isFeature
                      ? 'hva ønsker du, og hvorfor?'
                      : 'hva gjorde du, hva skjedde, og hva forventet du?'}
                  </p>
                )}
              </div>

              {/* Screenshot — delayed capture, upload or paste */}
              <div>
                <div className="font-display text-[11px] font-bold uppercase tracking-wider text-ink-2 mb-2">
                  {isFeature ? 'Skjermbilde (valgfritt)' : 'Skjermbilde — sterkt anbefalt for bugs'}
                </div>
                <div
                  className={`rounded-xl border border-dashed p-4 ${
                    isFeature ? 'border-ink-4 bg-bg-soft/40' : 'border-rose-300 bg-rose-50/40'
                  }`}
                >
                  {screenshot ? (
                    <div className="relative rounded-lg border border-line overflow-hidden bg-white">
                      <img
                        src={screenshot}
                        alt="Skjermbilde"
                        className="w-full max-h-64 object-contain"
                      />
                      <button
                        onClick={() => setScreenshot(null)}
                        className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 hover:bg-black/80 text-white"
                        title="Fjern skjermbilde"
                      >
                        <X size={14} />
                      </button>
                      <div className="px-3 py-1.5 bg-bg-soft/80 text-[10px] font-mono text-ink-3 text-center">
                        ~{Math.round(screenshot.length / 1024)} kB · Lagres med rapporten
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex gap-2 flex-wrap items-center">
                        <button
                          type="button"
                          onClick={handleAutoCapture}
                          disabled={capturing}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold text-white flex items-center gap-2 disabled:opacity-60 ${
                            isFeature
                              ? 'bg-teal-800 hover:bg-teal-900'
                              : 'bg-rose-600 hover:bg-rose-700'
                          }`}
                          title="Bruker nettleserens skjermdeling til å fange siden bak dette vinduet"
                        >
                          <Camera size={15} />
                          {capturing
                            ? 'Tar bilde…'
                            : captureDelay === 0
                              ? 'Ta skjermbilde'
                              : `Ta skjermbilde om ${captureDelay} s`}
                        </button>
                        {/* Delay before the frame is grabbed. The dialog hides itself
                            either way, so the picture is always the page behind it. */}
                        <div
                          className="inline-flex rounded-lg border border-line bg-white overflow-hidden"
                          role="group"
                          aria-label="Forsinkelse før skjermbildet tas"
                        >
                          {[0, 3, 5, 10].map((d) => (
                            <button
                              key={d}
                              type="button"
                              onClick={() => setCaptureDelay(d)}
                              disabled={capturing}
                              aria-pressed={captureDelay === d}
                              className={`px-3 py-2 text-xs font-semibold border-l border-line first:border-l-0 ${
                                captureDelay === d
                                  ? 'bg-teal-950 text-white'
                                  : 'text-ink-2 hover:bg-bg-soft'
                              }`}
                            >
                              {d === 0 ? 'Nå' : `${d} s`}
                            </button>
                          ))}
                        </div>
                        {/* The file input was `hidden` — display: none — so
                            «Last opp bilde» could not be reached from a
                            keyboard at all (#900). It is visually hidden
                            instead, still inside its label, and the label
                            draws the focus ring the input cannot show. */}
                        <label className="px-3.5 py-2 rounded-lg border border-line-strong bg-white text-sm font-semibold text-ink flex items-center gap-2 cursor-pointer hover:bg-bg-soft has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:[outline-color:var(--focus-ring)]">
                          <ImagePlus size={15} /> Last opp bilde
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            onChange={handleUpload}
                            className="sr-only"
                          />
                        </label>
                      </div>
                      <p className="text-xs text-ink-2 mt-3 leading-relaxed">
                        Dette vinduet forsvinner mens bildet tas, så det er{' '}
                        <strong className="text-ink">siden bak</strong> som kommer med. Med
                        forsinkelse rekker du å åpne en meny eller holde musen over feilen.
                        Nettleseren spør først om du vil dele fanen — velg{' '}
                        <strong className="text-ink">denne fanen</strong>. Du kan også lime inn et
                        bilde du har tatt selv ({isMac ? 'Cmd+Shift+4' : 'Win+Shift+S'}, så{' '}
                        <strong className="text-ink">{isMac ? 'Cmd+V' : 'Ctrl+V'}</strong> her).
                      </p>
                      {warnNoScreenshot && (
                        <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-800">
                          <AlertCircle size={13} className="flex-shrink-0" />
                          Ingen skjermbilde ennå — for bugs er et bilde ofte det som gjør feilen
                          mulig å finne.
                        </p>
                      )}
                    </>
                  )}
                  {screenshotError && (
                    <div className="mt-3 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-700">
                      {screenshotError}
                    </div>
                  )}
                </div>
              </div>

              {/* Claude prompt preview — collapsed: it is long now that the trail rides along */}
              <details>
                <summary className="cursor-pointer text-sm font-semibold text-teal-800 hover:text-teal-900">
                  Forhåndsvisning — dette får Claude ({claudePrompt.length.toLocaleString('no-NO')}{' '}
                  tegn)
                </summary>
                <div className="mt-2 rounded-lg border border-line p-3 bg-bg-soft/40 max-h-60 overflow-y-auto">
                  <pre className="text-[11px] font-mono text-ink-2 whitespace-pre-wrap leading-relaxed">
                    {claudePrompt}
                  </pre>
                </div>
              </details>
            </>
          ) : (
            <>
              {/* NOT SAVED, said first and plainly (2026-10-22): the server may
                  refuse a report now — filing closed, too many, too large —
                  and nothing below may read as if it had been kept. */}
              {!saved.locally && refusal && (
                <div
                  role="alert"
                  className="rounded-lg bg-rose-soft border border-rose/30 p-4 text-sm text-ink"
                >
                  {t(refusal)}
                </div>
              )}
              {/* After saving: show status. The browser sends no e-mail of its own
                  (#1142): the report is filed, and the API mails the heads-up. */}
              {saved.locally && (
                <div className="rounded-lg bg-lime-bg/40 border border-lime-deep/30 p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Check size={14} className="text-lime-deep" />
                    <span className="font-semibold text-sm">Bug-rapport lagret lokalt</span>
                  </div>
                  <p className="text-xs text-ink-2">Du finner den i Bug-arkivet via menyen.</p>
                </div>
              )}

              {/* Not filed: offer to send it by hand instead. A filed report needs
                  nothing more — the API mails the heads-up itself (#1142). */}
              {!saved.locally && (
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-2 block">
                    Send manuelt til {bugReportEmail}
                  </div>
                  <a
                    href={mailtoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-lime text-sm w-full justify-center inline-flex"
                    style={{ textDecoration: 'none' }}
                  >
                    📧 Åpne i e-postapp
                  </a>
                  <p className="text-[11px] text-ink-3 mt-1.5">
                    Åpner standard e-postapp. Hvis det ikke fungerer, bruk "Kopiér"-knappene under.
                  </p>
                </div>
              )}

              {/* Secondary: copy option */}
              <details
                className="rounded-lg border border-line"
                open={!saved.locally && !mailtoUrl}
              >
                <summary className="px-3 py-2 cursor-pointer text-xs font-mono uppercase tracking-wider text-ink-3 hover:bg-bg-soft">
                  {saved.locally ? 'Se innholdet som ble sendt' : 'Eller kopiér manuelt'}
                </summary>
                <div className="px-3 pb-3 space-y-3">
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
                        Adresse
                      </span>
                      <button
                        onClick={() => handleCopy(bugReportEmail)}
                        className="text-[11px] font-mono text-ink-3 hover:text-ink-2"
                      >
                        {copyStatus === 'copied' ? '✓ kopiert!' : '📋 kopiér'}
                      </button>
                    </div>
                    <div className="rounded-lg border border-line p-2 bg-bg-soft/40 font-mono text-xs">
                      {bugReportEmail}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
                        Emne
                      </span>
                      <button
                        onClick={() => handleCopy(`[Padel24 Bug] ${title}`)}
                        className="text-[11px] font-mono text-ink-3 hover:text-ink-2"
                      >
                        📋 kopiér
                      </button>
                    </div>
                    <div className="rounded-lg border border-line p-2 bg-bg-soft/40 font-mono text-xs">
                      [Padel24 Bug] {title}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
                        Innhold
                      </span>
                      <button
                        onClick={() => handleCopy(emailBody)}
                        className="text-[11px] font-mono text-ink-3 hover:text-ink-2"
                      >
                        {copyStatus === 'copied' ? '✓ kopiert!' : '📋 kopiér alt'}
                      </button>
                    </div>
                    <div className="rounded-lg border border-line p-3 bg-bg-soft/40 max-h-40 overflow-y-auto">
                      <pre className="text-[11px] font-mono text-ink-2 whitespace-pre-wrap leading-relaxed">
                        {emailBody}
                      </pre>
                    </div>
                  </div>
                </div>
              </details>

              <div className="rounded-lg border border-line p-3 text-[11px] text-ink-3">
                💡 <strong>Tips:</strong> Du kan også kopiére Claude-prompten og lime inn i et nytt
                Claude-chat for å fikse buggen direkte.
              </div>
            </>
          )}
        </div>

        {/* Footer — discard on the left, close/send on the right */}
        <div className="flex items-center gap-3 px-6 py-4 border-t border-line flex-shrink-0 flex-wrap">
          {!saved && hasDraft && (
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                onClick={discardDraft}
                className="text-sm font-semibold text-ink-2 hover:text-ink"
              >
                Forkast utkastet
              </button>
              <span className="hidden sm:inline text-xs text-ink-3">
                Lukker du, beholdes utkastet til du sender det.
              </span>
            </div>
          )}
          <div className="flex gap-2 ml-auto">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-line-strong bg-white text-sm font-semibold text-ink hover:bg-bg-soft"
            >
              Lukk
            </button>
            {!saved && (
              <button
                onClick={handleSave}
                disabled={!canSubmit}
                className={`px-4 py-2 rounded-lg text-sm font-semibold text-white flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                  isFeature ? 'bg-teal-800 hover:bg-teal-900' : 'bg-rose-600 hover:bg-rose-700'
                }`}
              >
                <Send size={15} />
                {submitting
                  ? 'Sender…'
                  : // «Send»: the report goes to the API, which mails the heads-up (#1142)
                    isFeature
                    ? 'Send forslag'
                    : 'Send bug'}
              </button>
            )}
          </div>
        </div>
      </Dialog>
    </div>
  );
}
